import { useState, type FormEvent } from "react";
import { motion } from "motion/react";
import { ArrowRight } from "lucide-react";
import { type Lang, type Translations } from "@/lib/i18n";

const FORMSPREE_WAITLIST = "https://formspree.io/f/xwvwprlw";
// CTA path-with-lang: see Hero.tsx for rationale.
const appUrl = (lang: Lang): string =>
  lang === "zh" ? "/app/login?lang=zh" : "/app/login";

export const WaitlistCTA = ({ tr, lang }: { tr: Translations; lang: Lang }) => {
  const [waitlistStatus, setWaitlistStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [waitlistEmail, setWaitlistEmail] = useState('');

  const handleWaitlistSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setWaitlistStatus('submitting');
    try {
      const res = await fetch(FORMSPREE_WAITLIST, {
        method: 'POST',
        body: JSON.stringify({ email: waitlistEmail }),
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
      });
      if (res.ok) { setWaitlistStatus('success'); setWaitlistEmail(''); }
      else { setWaitlistStatus('error'); }
    } catch { setWaitlistStatus('error'); }
  };

  return (
    <section id="cta" className="bg-black text-white py-32 px-6">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-4xl md:text-6xl mb-8 leading-tight font-landing-serif">{tr.cta.title}</h2>
        <a href={appUrl(lang)} className="inline-flex items-center gap-2 btn-gradient px-12 py-5 label-vogue hover:opacity-90 transition-all shadow-xl shadow-landing-accent/20 cursor-pointer no-underline mb-12">
          {tr.nav.tryFree} <ArrowRight className="w-4 h-4" />
        </a>
        {waitlistStatus === 'success' ? (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-2xl font-landing-serif mb-4">{tr.cta.successTitle}</p>
            <p className="text-white/60 label-vogue">{tr.cta.successDesc}</p>
          </motion.div>
        ) : (
          <form onSubmit={handleWaitlistSubmit} className="flex flex-col md:flex-row border border-white/20 focus-within:border-landing-accent transition-colors rounded-2xl overflow-hidden">
            <input type="email" name="email" required value={waitlistEmail} onChange={(e) => setWaitlistEmail(e.target.value)} placeholder={tr.cta.emailPlaceholder} aria-label={tr.cta.emailPlaceholder} className="bg-transparent border-none flex-grow p-6 text-sm label-vogue focus:ring-0 text-white placeholder:text-white/30" />
            <button type="submit" disabled={waitlistStatus === 'submitting'} className={`btn-gradient px-12 py-6 label-vogue transition-opacity flex items-center justify-center gap-2 cursor-pointer ${waitlistStatus === 'submitting' ? 'opacity-70' : 'hover:opacity-90'}`}>
              {waitlistStatus === 'submitting' ? tr.cta.submitting : waitlistStatus === 'error' ? tr.cta.errorRetry : <>{tr.cta.subscribeBtn} <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};
